<script setup>
import { ref } from 'vue';
import RetryFailedRequest from '@/components/widgets/RetryFailedRequest.vue';
import config from '@/config/general.js';

const show = ref(null);
const search = ref('');
const objectDestination = ref(null);
const itemsPerPage = ref(config.itemsPerPage);

const open = (object) => {
    show.value = true;
    objectDestination.value = object;
};
const close = () => (show.value = false);

defineExpose({
    open,
    close
});

const emit = defineEmits(['selected', 'retry']);
defineProps({
    items: Array,
    headers: Array,
    showRetryButton: Boolean,
    loading: Boolean,
    width: {},
    title: {
        type: String
    },
    totalItems: {
        type: Number
    }
});

const clickOnRow = (event, { item }) => {
    emit('selected', item, objectDestination.value);
    close();
};
</script>

<template>
    <v-dialog
        v-model="show"
        width="auto"
        :scrim="true"
        transition="dialog-bottom-transition"
    >
        <v-card :width="width">
            <v-card-title class="mt-3">
                <div class="d-flex align-center titleModal">
                    <v-btn
                        variant="tonal"
                        icon
                        dark
                        @click="show = false"
                        size="small"
                        class="close-button mr-4"
                        color="primary"
                    >
                        <v-icon size="large">mdi-arrow-left</v-icon>
                    </v-btn>

                    <span class="ml-1">{{ title }}</span>
                </div>
            </v-card-title>

            <v-card-text>
                <RetryFailedRequest
                    v-if="showRetryButton"
                    @retry="emit('retry')"
                ></RetryFailedRequest>

                <v-text-field
                    v-model="search"
                    :label="$t('Search')"
                    prepend-inner-icon="mdi-magnify"
                    variant="outlined"
                    hide-details
                    single-line
                    density="compact"
                    class="mb-5 search-field"
                ></v-text-field>

                <div class="table-scroll">
                    <v-data-table
                        v-model:items-per-page="itemsPerPage"
                        :loading="loading"
                        :headers="headers"
                        density="compact"
                        :items="items"
                        :search="search"
                        @click:row="clickOnRow"
                        :items-per-page-text="$t('Items per page')"
                    ></v-data-table>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<style lang="scss" scoped>
:deep(.v-data-table .v-table__wrapper > table thead > tr > th) {
    font-weight: bold;
}

:deep(.v-data-table .v-table__wrapper > table tbody > tr) {
    transition: background-color 0.3s;
    font-size: 0.875rem !important;

    &:hover {
        background-color: #b2e9ed !important;
        box-shadow:
            0 2px 4px rgba(0, 0, 0, 0.18),
            0 0 1px rgba(0, 0, 0, 0.18);
    }
}

:deep(.v-data-table .v-table__wrapper > table) {
    td,
    th {
        font-size: 0.875rem !important;
    }

    thead {
        position: sticky;
        top: 0;
        z-index: 1;
        background-color: white;

        th {
            position: sticky;
            top: 0;
            background-color: white;
            box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);
        }
    }
}

:deep(.search-field) {
    .v-field {
        min-height: 32px !important;
        margin-bottom: 0 !important;
    }

    .v-field__input {
        min-height: 28px !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        line-height: 28px !important;
        font-size: 0.875rem !important;
    }

    .v-field__label {
        font-size: 0.75rem !important;
        margin-top: -2px !important;
    }
}

.table-scroll {
    position: relative;
}

.table-scroll :deep(.v-data-table .v-table__wrapper > table thead) {
    position: sticky;
    top: 0;
    z-index: 20;
    background-color: var(--v-theme-surface, #fff);
}

.table-scroll :deep(.v-data-table .v-table__wrapper > table thead th) {
    position: sticky;
    top: 0;
    background-color: var(--v-theme-surface, #fff);
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);
}

.table-scroll :deep(.sticky-column) {
    position: sticky !important;
    z-index: 15;
    background: var(--v-theme-surface, #fff);
}
</style>
